import * as React from 'react'
import { Platform } from 'react-native'

import api from '@api/api'
import logger from '@utils/logger'

import * as AdminStore from '@store/admin/admin'
import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as UsersStore from '@store/users/users'
import * as PlanStore from '@store/plan/plan'
import * as EDLStore from '@store/edl/edl'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'
import * as I18nStore from '@i18n/i18n'
import * as FiltreStore from '@store/filtre/filtre'
import * as MetierStore from '@store/metier/metier'
import * as QuestionStore from '@store/question/question'
import useReducer from '@store/useReducer'

import useConnectivity from './useConnectivity'

import { orderManifestations } from '@screens/manifestations/manifestationsUtils'
import { orderTenants } from '@screens/tenants/tenantsUtils'

import { EtatWorkflowIncidentEnum, AuthScope, Langage } from '@api/enum'

import locationWorker from '../workers/location.worker'

declare interface StatusState {
  status: Status
  lastUpdate?: number // timestamp
}

export const OPEN_INCIDENT_STATES: EtatWorkflowIncidentEnum[] = [
  EtatWorkflowIncidentEnum.DEVIS_ENVOYE,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_DEVIS,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_PRISE_EN_CHARGE,
  EtatWorkflowIncidentEnum.EN_PAUSE,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION,
  EtatWorkflowIncidentEnum.EN_COURS_DE_TRAITEMENT,
  EtatWorkflowIncidentEnum.REFUS_PRESTATAIRE,
  EtatWorkflowIncidentEnum.CLOTUREE_PRESTATAIRE,
]

import { createEvent, createStore } from 'effector'

export const actions = {
  setStatus: createEvent<Status>('setStatus'),
  setLastUpdate: createEvent<number>('setLastUpdate'),
  resetLastUpdate: createEvent('resetLastUpdate'),
}

const initialState: StatusState = { status: 'fetched' }
export const store = createStore<StatusState>(initialState, { name: 'status' })
  .on(actions.setStatus, (s, status) => ({ ...s, status }))
  .on(actions.setLastUpdate, (s, lastUpdate) => ({ ...s, lastUpdate }))
  .on(actions.resetLastUpdate, (s) => ({ ...s, lastUpdate: initialState.lastUpdate }))

const resetData = () => {
  // reset data
  const { location, manifestation, activity, user } = FiltreStore.initialState.incident
  SiteStore.actions.resetSiteStats()
  SiteStore.actions.resetTenants()
  SiteStore.actions.resetManifestations()
  IncidentStore.actions.resetIncidents()
  FiltreStore.actions.setIncidentFilter({ location, manifestation, activity, user })
  AdminStore.actions.resetUsers()
  actions.resetLastUpdate()
}

const updateRequiredData = (siteId: string, scopes: AuthScope[]) => {
  // required call for home

  // manifestations
  const ManifAPI =
    scopes.includes(AuthScope.ManifestationsLecture) ||
    scopes.includes(AuthScope.ManifestationsTousLecture) ||
    scopes.includes(AuthScope.ManifestationsExposantLecture) ||
    scopes.includes(AuthScope.ManifestationsUtilisateurLecture)
      ? api.manifestation.all(siteId)
      : Promise.resolve()

  // incidents
  const IncidentAPI = scopes.includes(AuthScope.DemandesIncidentLecture)
    ? api.incident.search({ siteId, etatsIncident: OPEN_INCIDENT_STATES })
    : Promise.resolve()

  // locataires permanents
  const LocataireAPI = scopes.includes(AuthScope.LocatairesPermanentsLecture)
    ? api.locatairePermanent.all(siteId)
    : Promise.resolve()

  // statistique site
  const SiteStatAPI = scopes.includes(AuthScope.StatistiquesSiteLecture)
    ? api.incident.stats(siteId)
    : Promise.resolve()

  // handle exception to prevent entire group to reject
  const apis = [
    ManifAPI.catch(logger.error),
    IncidentAPI.catch(logger.error),
    LocataireAPI.catch(logger.error),
    SiteStatAPI.catch(logger.error),
  ] as const

  // group promises
  return Promise.all(apis)
    .then(([manifestations, incidents, locataires, stats]) => {
      if (manifestations) {
        const ordered = orderManifestations(manifestations)
        SiteStore.actions.setManifestations(ordered)
      }

      if (incidents) {
        IncidentStore.actions.setIncidents(incidents)
      }

      if (locataires) {
        const ordered = orderTenants(locataires)
        SiteStore.actions.setTenants(ordered)
      }

      if (stats) {
        SiteStore.actions.setSiteStats(stats)
      }
    })
    .then(() => {
      actions.setLastUpdate(Date.now())
      actions.setStatus('fetched')
    })
    .catch((err) => {
      logger.error(err)
      actions.setStatus('error')
    })
}

const updateGlobalData = (siteId: string, scopes: AuthScope[]) => {
  // optional call for home

  // localisations
  const LocAPI = api.localisation.all(siteId, true)

  // familles
  const FamilleAPI = api.familles.all(siteId, true)

  // societes
  const SocieteAPI = api.societe.all(siteId)

  // users
  const UserAPI = scopes.includes(AuthScope.SitesUtilisateurLecture) ? api.users.all() : Promise.resolve()

  // plan
  const PlanAPI = scopes.includes(AuthScope.DemandesPlanLecture) ? api.plan.all(siteId) : Promise.resolve()

  // edl
  const EdlAPI = scopes.includes(AuthScope.EdlSiteLecture) ? api.edl.all(siteId) : Promise.resolve()

  // metiers
  const MetierAPI =
    scopes.includes(AuthScope.ControleQualiteSiteLecture) ||
    scopes.includes(AuthScope.ControleQualitePrestataireLecture)
      ? api.controle.metier()
      : Promise.resolve()

  // questions controle qualite
  const QuestionAPI =
    scopes.includes(AuthScope.ControleQualiteSiteLecture) ||
    scopes.includes(AuthScope.ControleQualitePrestataireLecture)
      ? api.controle.question()
      : Promise.resolve()

  // users filter
  const FiltreUserAPI =
    scopes.includes(AuthScope.FiltresUtilisateursDeclarant) || scopes.includes(AuthScope.FiltresUtilisateursSociete)
      ? api.users.filtre(siteId)
      : Promise.resolve()

  // societe filter
  const FiltreSocieteAPI = scopes.includes(AuthScope.FiltresSocietesLecture)
    ? api.incident.societes(siteId)
    : Promise.resolve()

  // group promises
  return Promise.all([
    LocAPI.catch(logger.error),
    FamilleAPI.catch(logger.error),
    SocieteAPI.catch(logger.error),
    UserAPI.catch(logger.error),
    PlanAPI.catch(logger.error),
    EdlAPI.catch(logger.error),
    FiltreUserAPI.catch(logger.error),
    MetierAPI.catch(logger.error),
    QuestionAPI.catch(logger.error),
    FiltreSocieteAPI.catch(logger.error),
  ])
    .then(([places, familles, societes, users, plans, edls, filtreUsers, metiers, questions, societesIncident]) => {
      if (places) {
        LocationStore.actions.setPlaces(places)
      }

      if (familles) {
        FamilleStore.actions.setActivities(familles)
      }

      if (societes) {
        SocieteStore.actions.setServices(societes)
      }

      if (users) {
        UsersStore.actions.setUsers(users)
      }

      if (plans) {
        PlanStore.actions.setPlans(plans)
      }

      if (edls) {
        EDLStore.actions.setFinishedEDL(edls)
      }

      if (filtreUsers) {
        const currentUsers = FiltreStore.store.getState().users
        const knownIncidentUserIds = [...filtreUsers.users, ...currentUsers].map((u) => u.id)

        const missingIncidentUserIds = Array.from(
          new Set(IncidentStore.store.getState().incidents.map((inc) => inc.utilisateurDeclarantId))
        )
          .filter(Boolean)
          .filter((id) => !knownIncidentUserIds.includes(id))

        Promise.all(missingIncidentUserIds.map(api.users.get))
          .then((newUsers) => {
            FiltreStore.actions.setUsers([
              ...currentUsers.filter((u) => !filtreUsers.users.map((ut) => ut.id).includes(u.id)),
              ...filtreUsers.users,
              ...newUsers,
            ])
          })
          .catch(logger.error)
      }

      if (metiers) {
        MetierStore.actions.setMetiers(metiers.metiers)
      }

      if (questions) {
        QuestionStore.actions.setControleQuestions(questions.questions)
      }

      if (societesIncident) {
        FiltreStore.actions.setSocietesIncident(societesIncident)
      }
    })
    .catch(logger.error)
}

export const refresh = (siteId: string, scopes: AuthScope[]) => {
  actions.setStatus('loading')

  resetData()
  updateRequiredData(siteId, scopes).then(() => updateGlobalData(siteId, scopes))
}

export const useSiteChangeHandler = () => {
  const user = useReducer(UserStore.store, (s) => s.user)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const places = useReducer(LocationStore.store, (s) => s.places)

  const status = useReducer(store, (s) => s.status)

  const [online, loading] = useConnectivity()

  React.useEffect(() => {
    if (site && !!user && status !== 'loading') {
      refresh(site.id, scopes)
    }
  }, [!!user, site && site.id])

  React.useEffect(() => {
    // Reset site store infos on reconnect (only available for mobile)
    if (online && !loading && site && status !== 'loading' && Platform.OS !== 'web') {
      updateRequiredData(site.id, scopes).then(() => updateGlobalData(site.id, scopes))
    }
  }, [online, loading])

  React.useEffect(() => {
    if (places.length > 0) {
      locationWorker.run(places).then((tree) => {
        LocationStore.actions.setPlacesTree(tree)
      })
    }
  }, [places, status])

  React.useEffect(() => {
    // actions on user language update
    if (user && user.langage) {
      I18nStore.actions.setLang(user.langage === Langage.FR ? 'fr' : 'en')
    } else {
      I18nStore.actions.setLang(I18nStore.fallbackLang)
    }
  }, [user && user.langage])
}
